/**
 * Modo estudo (PLAN §22).
 *
 * MVP deliberadamente simples: embaralhar, esconder o nome, revelar, registrar
 * acerto ou erro. Repetição espaçada de verdade é pós-MVP e **não deve bloquear o
 * lançamento** — o plano diz isso explicitamente.
 *
 * O único refinamento aqui é a ordem: sinais que o usuário erra aparecem antes.
 * É barato e muda o resultado do estudo; um algoritmo de intervalos não seria
 * nenhuma das duas coisas.
 */
import { db, type StudySession } from '@/db/schema';
import { recordStudyResult } from '@/db/repositories/progress';
import type { LocalSignState, Sign } from '@/types/catalog';

export interface StudyFilters {
  categoryIds?: string[];
  onlyFavorites?: boolean;
  onlyOffline?: boolean;
}

export interface StudyDeckOptions extends StudyFilters {
  limit?: number;
  /** Injetável para tornar o embaralhamento determinístico nos testes. */
  random?: () => number;
}

/**
 * Embaralhamento Fisher-Yates.
 *
 * `sort(() => Math.random() - 0.5)` é o atalho comum e é enviesado: a ordem
 * resultante não é uniforme, e alguns sinais apareceriam sistematicamente mais.
 */
export function shuffle<T>(items: T[], random: () => number = Math.random): T[] {
  const result = [...items];
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [result[i], result[j]] = [result[j]!, result[i]!];
  }
  return result;
}

/**
 * Monta o baralho da sessão.
 *
 * Os sinais mais errados vêm primeiro, e dentro de cada faixa de score a ordem é
 * aleatória — sem isso o usuário decoraria a sequência em vez do sinal.
 */
export function buildDeck(
  signs: Sign[],
  states: Map<string, LocalSignState>,
  options: StudyDeckOptions = {},
): Sign[] {
  const { limit = 20, random = Math.random } = options;

  const eligible = signs.filter((sign) => {
    if (!sign.active) return false;

    const state = states.get(sign.id);
    if (options.onlyFavorites && !state?.favorite) return false;
    if (options.onlyOffline && state?.offline.status !== 'available') return false;
    if (options.categoryIds?.length && !options.categoryIds.includes(sign.categoryId)) return false;

    return true;
  });

  // Agrupa por score e embaralha dentro do grupo: prioriza o que é difícil sem
  // tornar a sequência previsível.
  const byScore = new Map<number, Sign[]>();
  for (const sign of eligible) {
    const score = states.get(sign.id)?.studyScore ?? 0;
    byScore.set(score, [...(byScore.get(score) ?? []), sign]);
  }

  const ordered = [...byScore.entries()]
    .sort(([a], [b]) => a - b)
    .flatMap(([, group]) => shuffle(group, random));

  return ordered.slice(0, limit);
}

export async function loadStudyDeck(options: StudyDeckOptions = {}): Promise<Sign[]> {
  const signs = await db.signs.toArray();
  const states = new Map((await db.localSignState.toArray()).map((state) => [state.signId, state]));
  return buildDeck(signs, states, options);
}

export interface SessionResult {
  total: number;
  correct: number;
  incorrect: number;
  /** 0..1 */
  accuracy: number;
}

export async function startSession(filters: StudyFilters, total: number): Promise<string> {
  const id = `study-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  const session: StudySession = {
    id,
    startedAt: new Date().toISOString(),
    categoryIds: filters.categoryIds ?? [],
    onlyFavorites: filters.onlyFavorites ?? false,
    onlyOffline: filters.onlyOffline ?? false,
    total,
    correct: 0,
    incorrect: 0,
  };

  await db.studySessions.put(session);
  return id;
}

/** Registra uma resposta, atualizando a sessão e o progresso do sinal. */
export async function answer(
  sessionId: string,
  signId: string,
  correct: boolean,
): Promise<void> {
  await recordStudyResult(signId, correct);

  await db.transaction('rw', db.studySessions, async () => {
    const session = await db.studySessions.get(sessionId);
    if (!session) return;

    await db.studySessions.put({
      ...session,
      correct: session.correct + (correct ? 1 : 0),
      incorrect: session.incorrect + (correct ? 0 : 1),
    });
  });
}

export async function finishSession(sessionId: string): Promise<SessionResult | null> {
  const session = await db.studySessions.get(sessionId);
  if (!session) return null;

  await db.studySessions.put({ ...session, finishedAt: new Date().toISOString() });

  const answered = session.correct + session.incorrect;
  return {
    total: session.total,
    correct: session.correct,
    incorrect: session.incorrect,
    accuracy: answered > 0 ? session.correct / answered : 0,
  };
}

export async function recentSessions(limit = 10): Promise<StudySession[]> {
  return db.studySessions.orderBy('startedAt').reverse().limit(limit).toArray();
}
